// Hide departures the agency's own clock has already passed.
//
// The server builds each card's departure list when the trip cache is warmed and
// keeps it for up to a day, so a card can otherwise still advertise this
// morning's departure. Each departure carries its own cut-off minute, but a
// resolved "now" is never rendered into the cached fragment — that is read live
// from the agency's zone here, so the clock stays current however old the HTML is.
//
// Lives in its own module rather than inside storefront-search.js's IIFE so the
// date comparison can be tested directly.

/**
 * The agency's current wall clock as `YYYY-MM-DD HH:MM`.
 *
 * `en-CA` is the locale that formats the date that way, which is also how the
 * server writes `data-bookable-until` — so the two compare as plain strings.
 *
 * @param {string} timeZone IANA zone name, e.g. "Pacific/Apia".
 * @param {Date} now Injectable for tests.
 * @returns {string|null} null when the zone is unusable.
 */
export function agencyClockNow(timeZone, now = new Date()) {
    try {
        const parts = new Intl.DateTimeFormat("en-CA", {
            timeZone: timeZone || "UTC",
            hourCycle: "h23",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        }).formatToParts(now);
        const at = {};
        parts.forEach((part) => (at[part.type] = part.value));

        return `${at.year}-${at.month}-${at.day} ${at.hour}:${at.minute}`;
    } catch (e) {
        // A zone the browser cannot resolve is a misconfigured agency, not a
        // visitor problem. Say so rather than silently skipping the filter.
        console.warn("[tc-search] unusable agency timezone:", timeZone, e);

        return null;
    }
}

/**
 * Drop the cloak that hides a departure list behind its pulse.
 *
 * Separate from the filtering so it can run unconditionally: a list shown
 * unfiltered beats one that pulses forever.
 *
 * @param {ParentNode} root
 */
export function revealDepartures(root) {
    (root || document)
        .querySelectorAll("[data-departures-pending]")
        .forEach((el) => el.removeAttribute("data-departures-pending"));
}

/**
 * Whether the agency's clock has passed this departure.
 *
 * `data-bookable-until` is the last minute the departure can still be booked:
 * its final time of day, less the trip's booking buffer. A fragment rendered
 * before departures carried one knows only the day, so it falls back to hiding
 * yesterday — the behaviour it shipped with.
 *
 * @param {Element} item
 * @param {string} clockNow `YYYY-MM-DD HH:MM` on the agency's clock.
 */
function hasPassed(item, clockNow) {
    const until = item.getAttribute("data-bookable-until");

    return until
        ? until < clockNow
        : item.getAttribute("data-departs-at") < clockNow.slice(0, 10);
}

/**
 * Hide passed departures within `root`, then reveal what is left.
 *
 * @param {ParentNode} root
 * @param {Date} now Injectable for tests.
 */
export function hidePassedDepartures(root, now = new Date()) {
    if (!root) {
        return;
    }

    root.querySelectorAll(".departure-list[data-agency-timezone]").forEach(
        (list) => {
            const clockNow = agencyClockNow(
                list.getAttribute("data-agency-timezone"),
                now,
            );

            if (!clockNow) {
                return; // unusable zone: leave the server's list alone
            }

            const items = list.querySelectorAll("li[data-departs-at]");
            let remaining = items.length;

            items.forEach((item) => {
                if (hasPassed(item, clockNow)) {
                    item.hidden = true;
                    remaining -= 1;
                }
            });

            // A "Next departures" heading above an empty list reads as a bug.
            const block = list.closest(".next-departure");
            if (block && remaining === 0) {
                block.hidden = true;
            }
        },
    );

    revealDepartures(root);
}
